import { CanvasData } from '../../../../../../types/healthcare/TreatmentPlanTypes';

/**
 * Frontend-only visual merge of watcher nodes into the stage they watch.
 *
 * A watcher is a real, separate stage in the backend (no backend/data-model change is made here).
 * For the practitioner's plan modal we present it as part of the stage it follows: this helper maps
 * each watcher node to its predecessor ("the thing it is watching for") using the canvas edges, so
 * the stage list can hide the standalone watcher row and the action view can surface the watcher's
 * state inline.
 *
 * A watcher's parent is the source of the edge into it. If a watcher has multiple incoming edges
 * (unusual) the first is used; watchers with no predecessor are left un-merged (rendered standalone).
 */
export function buildWatcherParentMap(canvas: CanvasData | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!canvas?.nodes || !canvas?.edges) return map;

  const watcherNodeIds = new Set(
    canvas.nodes.filter((n) => n.data?.stage_type === 'watcher').map((n) => n.id),
  );
  if (watcherNodeIds.size === 0) return map;

  for (const edge of canvas.edges) {
    if (watcherNodeIds.has(edge.target) && !map.has(edge.target)) {
      map.set(edge.target, edge.source);
    }
  }
  return map;
}

/** Inverse of {@link buildWatcherParentMap}: parent stage key → its watcher child stage key. */
export function buildWatcherChildMap(canvas: CanvasData | null | undefined): Map<string, string> {
  const inverse = new Map<string, string>();
  for (const [watcherKey, parentKey] of buildWatcherParentMap(canvas)) {
    if (!inverse.has(parentKey)) inverse.set(parentKey, watcherKey);
  }
  return inverse;
}
